import type {
  AnswerInput,
  DuelView,
  GameMode,
  SessionSummary,
  StatsView,
  Task,
  TrainingConfig,
  DailyState,
} from '@tm/shared';
import { initData } from './telegram.js';

export interface Me {
  user: { id: number; firstName: string; username: string; bestSprintScore: number; bestCombo: number };
  config: TrainingConfig;
  settings: {
    minTable: number;
    maxTable: number;
    division: boolean;
    dailyGoal: number;
    reminderHour: number;
  };
  daily: DailyState;
  ladderLevel: number;
  duels: DuelView[];
  joinedDuel: DuelView | null;
}

export interface StartedSession {
  sessionId: string;
  mode: GameMode;
  tasks: Task[];
  config: TrainingConfig;
  timeLimitMs: number;
}

/**
 * Вне Telegram подписи initData нет и сервер обоснованно отвечает 401.
 * Чтобы вёрстку можно было смотреть в обычном браузере, в режиме разработки
 * подставляем демо-данные. В продакшен-сборку эта ветка не попадает.
 */
async function mocked<T>(path: string): Promise<T | null> {
  if (!import.meta.env.DEV || initData()) return null;
  const { mockMe, mockStats } = await import('./mock.js');
  if (path === '/me') return mockMe() as T;
  if (path === '/stats') return mockStats() as T;
  return null;
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  const demo = await mocked<T>(path);
  if (demo) return demo;

  const res = await fetch(`/api${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Подписанная строка Telegram — единственное удостоверение личности.
      'X-Init-Data': initData(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text();
    let message = `Ошибка ${res.status}`;
    try {
      message = (JSON.parse(text) as { error?: string }).error ?? message;
    } catch {
      /* тело не JSON — оставляем код статуса */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () => call<Me>('/me'),
  stats: () => call<StatsView>('/stats'),
  startSession: (mode: GameMode, opts: { table?: number; duelId?: string } = {}) =>
    call<StartedSession>('/session/start', { mode, ...opts }),
  finishSession: (sessionId: string, answers: AnswerInput[]) =>
    call<SessionSummary>('/session/finish', { sessionId, answers }),
  createDuel: () =>
    call<{ duel: DuelView; shareUrl: string; chatUrl: string }>('/duel/create', {}),
  joinDuel: (id: string) => call<DuelView>('/duel/join', { id }),
  duels: () => call<DuelView[]>('/duels'),
  saveSettings: (patch: Partial<Me['settings']>) => call<Me['settings']>('/settings', patch),
};
